// Older invoices that went out cleanly. With the five outbound-invoice rows below they run the
// log past one page, so the panel's page controls render in every story that shows it.
const sentInvoiceHistory = Array.from({ length: 8 }, (_, i) => {
  const invoiceIdentifier = `BOL549${i}`;
  const day = String(21 - i).padStart(2, "0");
  return {
    ediOutboundInvoiceId: `edi-210-history-${i + 1}`,
    customerInvoiceId: `customer-invoice-history-${i + 1}`,
    invoiceIdentifier,
    shipmentId: `shipment-history-${i + 1}`,
    kind: "invoice",
    state: "sent",
    attempts: 1,
    filename: `FS_210_${invoiceIdentifier}_202607${day}101500.edi`,
    sentAt: `2026-07-${day}T10:15:00Z`,
    lastError: undefined,
    createdAt: `2026-07-${day}T10:10:00Z`,
  };
});

// The log endpoints answer one page and how many rows match, like the server's first page of 10
// with no search or filter. More rows than fit, so the page controls render.
function page(rows: unknown[]): { rows: unknown[]; totalCount: number } {
  return { rows: rows.slice(0, 10), totalCount: rows.length };
}

/**
 * Inline MSW responses for the outbound EDI endpoints (company Integrations → EDI tab).
 * Spread into INLINE_RESPONSES in inlineResponses.tsx.
 */
export const EDI_INLINE_RESPONSES: Record<
  string,
  Record<string, unknown> | unknown[]
> = {
  // A live partner on FTPS: connection-tested and delivering.
  // Wrapped, because a company with nothing saved answers 200 with an absent `settings` rather
  // than an error.
  "/apollo/edi/delivery-settings": {
    settings: {
      companyId: "f3643250-3a72-483b-babb-784a67a16c3d",
      protocol: "ftps",
      host: "ftp1.isnweb.com",
      port: 21,
      username: "frs10389",
      passwordSet: true,
      directory: "/Inbox",
      trackingDirectory: undefined,
      invoiceSendTiming: "after_first_tracking",
      enabled: true,
      lastSuccessAt: "2026-07-24T14:15:00Z",
      lastError: undefined,
      lastErrorAt: undefined,
    },
  },

  // One of each kind and state, so every badge and the retry row action render, then enough sent
  // history to need a second page.
  "/apollo/edi/outbound-invoices": page([
    {
      ediOutboundInvoiceId: "c1111111-0000-0000-0000-000000000001",
      customerInvoiceId: "d1111111-0000-0000-0000-000000000001",
      invoiceIdentifier: "BOL5501",
      shipmentId: "e1111111-0000-0000-0000-000000000001",
      kind: "invoice",
      state: "sent",
      attempts: 1,
      filename: "FS_210_BOL5501_20260724141500.edi",
      sentAt: "2026-07-24T14:15:00Z",
      lastError: undefined,
      createdAt: "2026-07-24T14:10:00Z",
    },
    {
      ediOutboundInvoiceId: "c1111111-0000-0000-0000-000000000002",
      customerInvoiceId: "d1111111-0000-0000-0000-000000000002",
      invoiceIdentifier: "BOL5502",
      shipmentId: "e1111111-0000-0000-0000-000000000002",
      kind: "credit",
      state: "pending",
      attempts: 0,
      filename: undefined,
      sentAt: undefined,
      lastError: undefined,
      createdAt: "2026-07-24T13:40:00Z",
    },
    {
      ediOutboundInvoiceId: "c1111111-0000-0000-0000-000000000003",
      customerInvoiceId: "d1111111-0000-0000-0000-000000000003",
      invoiceIdentifier: "BOL5503",
      shipmentId: "e1111111-0000-0000-0000-000000000003",
      kind: "correction",
      state: "failed",
      attempts: 24,
      filename: undefined,
      sentAt: undefined,
      lastError: "Exhausted available authentication methods",
      createdAt: "2026-07-23T09:05:00Z",
    },
    // Booked this morning, not picked up yet: held until the shipment's first 214 goes out.
    {
      ediOutboundInvoiceId: "c1111111-0000-0000-0000-000000000005",
      customerInvoiceId: "d1111111-0000-0000-0000-000000000005",
      invoiceIdentifier: "CE2QERZK",
      shipmentId: "e1111111-0000-0000-0000-000000000005",
      kind: "invoice",
      state: "awaiting_tracking",
      attempts: 0,
      filename: undefined,
      sentAt: undefined,
      lastError: undefined,
      createdAt: "2026-07-24T15:29:59Z",
    },
    {
      ediOutboundInvoiceId: "c1111111-0000-0000-0000-000000000004",
      customerInvoiceId: "d1111111-0000-0000-0000-000000000004",
      invoiceIdentifier: "BOL5504",
      shipmentId: "e1111111-0000-0000-0000-000000000004",
      kind: "invoice",
      state: "cancelled",
      attempts: 0,
      filename: undefined,
      sentAt: undefined,
      lastError: undefined,
      createdAt: "2026-07-22T16:20:00Z",
    },
    ...sentInvoiceHistory,
  ]),

  // One of each reason and state, so every badge and the retry row action render. A file goes out
  // whenever what it would say changes, so a moving shipment has several, each a full snapshot of
  // its tracking: the sent rows carry the status count it held. The superseded row is an older
  // unsent file a newer one replaced.
  "/apollo/edi/outbound-tracking": page([
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000001",
      shipmentId: "e1111111-0000-0000-0000-000000000001",
      proNumber: "91059232",
      reason: "delivered",
      expectedDelivery: "2026-07-24",
      state: "sent",
      attempts: 1,
      filename: "FS_214_91059232_20260724141500.edi",
      sentEventCount: 9,
      sentAt: "2026-07-24T14:15:00Z",
      lastError: undefined,
      createdAt: "2026-07-24T14:10:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000002",
      shipmentId: "e1111111-0000-0000-0000-000000000001",
      proNumber: "91059232",
      reason: "eta_changed",
      expectedDelivery: "2026-07-24",
      state: "sent",
      attempts: 1,
      filename: "FS_214_91059232_20260723164005.edi",
      sentEventCount: 6,
      sentAt: "2026-07-23T16:40:05Z",
      lastError: undefined,
      createdAt: "2026-07-23T16:40:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000003",
      shipmentId: "e1111111-0000-0000-0000-000000000001",
      proNumber: "91059232",
      reason: "tracking_update",
      expectedDelivery: "2026-07-23",
      state: "sent",
      attempts: 1,
      filename: "FS_214_91059232_20260723080204.edi",
      sentEventCount: 6,
      sentAt: "2026-07-23T08:02:04Z",
      lastError: undefined,
      createdAt: "2026-07-23T08:02:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000004",
      shipmentId: "e1111111-0000-0000-0000-000000000001",
      proNumber: "91059232",
      reason: "in_transit",
      expectedDelivery: "2026-07-23",
      state: "sent",
      attempts: 1,
      filename: "FS_214_91059232_20260722091200.edi",
      sentEventCount: 4,
      sentAt: "2026-07-22T09:12:00Z",
      lastError: undefined,
      createdAt: "2026-07-22T09:10:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000005",
      shipmentId: "e1111111-0000-0000-0000-000000000002",
      proNumber: "91059240",
      reason: "tracking_update",
      expectedDelivery: "2026-07-26",
      state: "failed",
      attempts: 3,
      filename: undefined,
      sentEventCount: undefined,
      sentAt: undefined,
      lastError: "Auth fail",
      createdAt: "2026-07-24T08:05:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000006",
      shipmentId: "e1111111-0000-0000-0000-000000000002",
      proNumber: "91059240",
      reason: "in_transit",
      expectedDelivery: "2026-07-25",
      state: "superseded",
      attempts: 1,
      filename: undefined,
      sentEventCount: undefined,
      sentAt: undefined,
      lastError: "Auth fail",
      createdAt: "2026-07-23T11:20:00Z",
    },
    {
      ediOutboundTrackingId: "f1111111-0000-0000-0000-000000000007",
      shipmentId: "e1111111-0000-0000-0000-000000000003",
      proNumber: "91059241",
      reason: "delivered",
      expectedDelivery: undefined,
      state: "pending",
      attempts: 0,
      filename: undefined,
      sentEventCount: undefined,
      sentAt: undefined,
      lastError: undefined,
      createdAt: "2026-07-24T07:30:00Z",
    },
  ]),

  "/apollo/edi/test-delivery-connection": {
    success: true,
    entriesFound: 3,
    loginDirectory: "/",
    error: undefined,
  },
};
